import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from "@apollo/client"
import { CombinedGraphQLErrors, ServerError } from "@apollo/client/errors"
import { ErrorLink } from "@apollo/client/link/error"

import { captureException } from "@shared/lib/sentry"

import { IGQLApiError } from "./types"
import { GRAPHQL_API_PATH } from "../config"
import { ERROR_500_TEXT, HttpStatus } from "../constants/errorMessages"
import CommonApiError from "../errors/CommonApiError"

type GraphQLHttpErrorResponse = {
    detail?: unknown
    errors?: Array<IGQLApiError & { extensions?: Record<string, unknown> }>
}

const flattenGraphQLErrors = (
    errors: ReadonlyArray<{ extensions?: Record<string, unknown>, message: string }>,
) => errors.map(({ extensions, ...error }) => ({ ...error, ...extensions }))

const getResponseBody = (bodyText: string): unknown => {
    try {
        return JSON.parse(bodyText) as GraphQLHttpErrorResponse
    } catch {
        return bodyText
    }
}

/**
 * Keeps the existing API error contract while Apollo handles GraphQL transport.
 */
export const normalizeApolloError = (error: unknown): unknown => {
    if (CombinedGraphQLErrors.is(error)) {
        const errors = flattenGraphQLErrors(error.errors)
        return new CommonApiError(errors, "GraphQL Error")
    }

    if (ServerError.is(error)) {
        const responseBody = getResponseBody(error.bodyText)
        const response = responseBody as GraphQLHttpErrorResponse
        const body = response?.errors
            ? flattenGraphQLErrors(response.errors)
            : response?.detail ?? responseBody
        const message = error.statusCode === HttpStatus.InternalServerError
            ? ERROR_500_TEXT
            : `HTTP ${error.statusCode}`

        return new CommonApiError(body, message)
    }

    return error
}

const httpLink = new HttpLink({
    credentials: 'omit',
    headers: {
        Accept: 'application/graphql-response+json, application/json',
        'Content-Type': 'application/json',
    },
    uri: GRAPHQL_API_PATH,
})

const sentryErrorLink = new ErrorLink(({ error, operation }) => {
    captureException(error, {
        api: 'graphql',
        operationName: operation.operationName ?? 'anonymous',
        operationType: operation.operationType,
        url: GRAPHQL_API_PATH,
    })
})

const apolloClient = new ApolloClient({
    cache: new InMemoryCache(),
    link: ApolloLink.from([sentryErrorLink, httpLink]),
    queryDeduplication: false,
})

export default apolloClient
